import React from 'react';
import { DataTableCell, DataTableRow } from '@amzn/meridian/data-table';
import Column from '@amzn/meridian/column';
import Link from '@amzn/meridian/link';
import Text from '@amzn/meridian/text';
import { DateTime } from 'luxon';
import { useTranslation } from 'src/arb-wrapper';
import { EMPTY_TABLE_VALUE } from 'src/constants';
import { type SyncTargetDto } from 'src/types/sync-target.types';
import { formatUtcDateTimeWithZone } from 'src/utils/formatDateTime';
import { normalizeEnumeration } from 'src/utils/boxFolderProgress';
import { boxFolderUrl, formatRefreshSchedule } from 'src/utils/syncTarget';
import { BoxFolderActionsMenu } from 'src/components/box-folders/BoxFolderActionsMenu';
import { BoxFolderExclusionsCell } from 'src/components/box-folders/BoxFolderExclusionsCell';
import { BoxFolderStatusTag } from 'src/components/box-folders/BoxFolderStatusTag';
import { BoxFolderSyncProgress } from 'src/components/box-folders/BoxFolderSyncProgress';

/** Absolute timestamp plus a relative hint, matching the campaign table's date cells. */
const DateCell: React.FC<{ iso?: string }> = ({ iso }) => {
  const formatted = formatUtcDateTimeWithZone(iso, EMPTY_TABLE_VALUE);
  if (formatted === EMPTY_TABLE_VALUE) {
    return <Text>{EMPTY_TABLE_VALUE}</Text>;
  }

  const relative = iso ? DateTime.fromISO(iso, { zone: 'utc' }).toRelativeCalendar() : undefined;

  return (
    <Column spacing="100">
      <Text>{formatted}</Text>
      {relative && <Text color="secondary" type="b200">{relative}</Text>}
    </Column>
  );
};

interface BoxFolderTableRowProps {
  syncTarget: SyncTargetDto;
  onEditExclusions: (syncTarget: SyncTargetDto) => void;
}

/**
 * One enrolled Box folder.
 *
 * Deliberately omits Bender's composite `box:<id>` registry key, which is plumbing this
 * page hides. The folder ID links out to Box, which is the identifier a user actually
 * recognizes.
 *
 * Status and sync progress are two adjacent cells, never one. Enumeration `completedAt` —
 * the "Last synced" column — only says discovery finished; the progress cell is what says
 * how far the folder's files have actually been checked.
 */
export const BoxFolderTableRow: React.FC<BoxFolderTableRowProps> = ({
  syncTarget,
  onEditExclusions,
}) => {
  const { t } = useTranslation();
  const { targetId, status, errorType, errorAt, createdAt, userAlias } = syncTarget;
  const { completedAt } = normalizeEnumeration(syncTarget);

  return (
    <DataTableRow>
      <DataTableCell>
        <Link
          data-testid={`box-folder-${targetId}-link`}
          href={boxFolderUrl(targetId)}
          rel="noopener noreferrer"
          target="_blank"
        >
          {targetId}
        </Link>
      </DataTableCell>
      <DataTableCell>
        <BoxFolderStatusTag
          errorAt={errorAt}
          errorType={errorType}
          status={status}
          testId={`box-folder-${targetId}-status`}
        />
      </DataTableCell>
      {/*
        Sits beside the status tag rather than inside it. Qualification carries on after
        Bender has already set the row back to ACTIVE, so "Active" and "Syncing assets" are
        the normal simultaneous state of a folder — see BoxFolderSyncProgress.
      */}
      <DataTableCell data-testid={`box-folder-${targetId}-progress`}>
        <BoxFolderSyncProgress syncTarget={syncTarget} testId={`box-folder-${targetId}-progress-cell`} />
      </DataTableCell>
      <DataTableCell data-testid={`box-folder-${targetId}-last-synced`}>
        <DateCell iso={completedAt} />
      </DataTableCell>
      <DataTableCell data-testid={`box-folder-${targetId}-sync-schedule`}>
        <Text>{formatRefreshSchedule(syncTarget.refreshSchedule, t)}</Text>
      </DataTableCell>
      <DataTableCell>
        <Text>{userAlias}</Text>
      </DataTableCell>
      <DataTableCell>
        <DateCell iso={createdAt} />
      </DataTableCell>
      <DataTableCell data-testid={`box-folder-${targetId}-excluded-folders`}>
        <BoxFolderExclusionsCell entries={syncTarget.excludedFolderIds} />
      </DataTableCell>
      <DataTableCell data-testid={`box-folder-${targetId}-excluded-patterns`}>
        <BoxFolderExclusionsCell entries={syncTarget.excludedPathnames} />
      </DataTableCell>
      <DataTableCell>
        <BoxFolderActionsMenu
          syncTarget={syncTarget}
          testIdPrefix={`box-folder-${targetId}`}
          onEditExclusions={() => { onEditExclusions(syncTarget); }}
        />
      </DataTableCell>
    </DataTableRow>
  );
};
